import { DndContext, DragEndEvent, DragOverlay } from '@dnd-kit/core';
import { Box, Button, Modal, Text } from '@fozito/common-ui';
import clsx from 'clsx';
import { last, remove, sumBy } from 'lodash';
import { FooterActions } from 'modules/POS/Cashier/components/styles';
import cashierApis from 'modules/POS/services/apis';
import { IApiSplitBillBody, IApiSplitBillValueItem, IBillInfoItemValue, IBillInfoResData } from 'modules/POS/services/types/bill';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { IResponseData } from 'services/response';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import { v4 as uuid } from 'uuid';
import { setPageLoading } from 'widgets/PageLoading';
import { Draggable } from '../DragAndDrop/Draggable';
import { Droppable } from '../DragAndDrop/Droppable';
import { useAppDispatch } from 'store/hooks';
import cashierService from 'modules/POS/services';
// type DraggableData = {
//   service: IService;
//   staff: IStaffTicketResItem;
// };
type IItemValue = {
  itemId: string,
  itemName: string,
  itemType: string;
  price: number,
  discount: number,
  quantity: number,
  uuid_local: string;
  expiredDate?: IBillInfoItemValue['expiredDate']
};
type DraggableData = {
  service: IItemValue;
};
type ISplitTicketItem = {
  billId: string;
  items: IItemValue[];
  customerName: string;
  totalPrice: number;
  customerPhone: string;
  ticketNumber: string;
  isDraft?: boolean;
};
const geneNewTicket = () => {
  const result: ISplitTicketItem = {
    billId: uuid(),
    customerName: 'New Ticket',
    totalPrice: 0,
    items: [],
    customerPhone: '',
    ticketNumber: '',
    isDraft: true,
  };
  return result;
};

type ISplitButtonProps = {
  onComplete: (billId: string) => void;
};
type Ref = {
  open: (billId: string) => void;
};
export const useModalSplitTicket = () => useRef<Ref>(null);
const ModalSplitTicket = forwardRef<Ref, ISplitButtonProps>(({ onComplete }, ref) => {
  const dispatch = useAppDispatch();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<IBillInfoResData | null>(null);
  const [ticketDetail, setTicketDetail] = useState<ISplitTicketItem | null>(null);
  const [newTickets, setNewTickets] = useState<ISplitTicketItem[]>([geneNewTicket(), geneNewTicket()]);

  const handleShowModal: Ref['open'] = async (billId: string) => {
    setPageLoading(true);
    try {
      const res: IResponseData<IBillInfoResData> = await cashierApis.getBillById(billId);
      const _detail = res.data.data;
      if (_detail) {
        setOpen(true);
        setDetail(_detail);
        setTicketDetail({
          billId: _detail.billId,
          customerName: _detail.customerName,
          customerPhone: _detail.customerPhone,
          items: _detail.items.map(o => ({
            itemId: o.itemId,
            itemType: o.itemType,
            itemName: o.itemName,
            price: o.price,
            discount: o.discount,
            uuid_local: uuid(),
            quantity: o.quantity,
            expiredDate: o.expiredDate,
          })),
          ticketNumber: _detail.ticketNumber?.toString() || '',
          totalPrice: detail?.total || 0,
        });
        setNewTickets([geneNewTicket(), geneNewTicket(), geneNewTicket()]);
      }
    } catch (error) {
    } finally {
      setPageLoading(false);
    }
  };

  const handleCloseModal = () => {
    setOpen(false);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (ticketDetail?.items?.length === 1) return;

    const { over, active } = event;
    const activeData = active.data.current || {};
    if (!activeData || !over) return;
    const { service } = activeData as DraggableData;
    setNewTickets(newTickets.map(o => {
      if (o.billId === over?.id) {
        const items = [...o.items];
        items.push(service);
        return ({
          ...o,
          items,
        });
      }
      return o;
    }));

    const _items = [...ticketDetail?.items || []];
    const exist = _items.find(_staff => _staff.uuid_local === service.uuid_local);
    if (exist) {
      remove(_items, _staff => _staff.uuid_local === service.uuid_local);
      setTicketDetail({
        ...ticketDetail,
        items: _items,
      } as ISplitTicketItem);

    }
  };

  const handleAddNewTicket = () => {
    if (last(newTickets)?.items.length === 0) return;
    const _newTickets = [...newTickets];
    _newTickets.push(geneNewTicket());
    setNewTickets(_newTickets);
    setTimeout(() => {
      scrollRef.current?.scrollBy({ behavior: 'smooth', left: -Number.MIN_SAFE_INTEGER });
    }, 0);
  };

  const handleSubmitModal = async () => {
    const body: IApiSplitBillBody = {
      billCurrent: {
        billId: ticketDetail?.billId || '',
        items: ticketDetail?.items.map(o => ({
          itemId: o.itemId,
          itemName: o.itemName,
          expiredDate: o.expiredDate,
          price: o.price,
          itemType: o.itemType,
          quantity: o.quantity,
          discount: o.discount,
        } as IApiSplitBillValueItem)) || []
      },
      newBills: newTickets.filter(o => o.items.length).map((_detail) => ({
        billId: _detail?.billId || '',
        items: _detail?.items.map(o => ({
          itemId: o.itemId,
          itemName: o.itemName,
          expiredDate: o.expiredDate,
          price: o.price,
          itemType: o.itemType,
          quantity: o.quantity,
          discount: o.discount,
        } as IApiSplitBillValueItem)) || []
      }))
    };

    setOpen(false);
    setPageLoading(true);
    try {
      const res: IResponseData<{ billId: string }> = await cashierApis.splitTicket(body);
      if (res.data.data.billId) {
        dispatch(cashierService.actions.async.lstBillOnHold.fetch());
        onComplete(res.data.data.billId);
      }
    } catch (error) {
    } finally {
      setPageLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    open: handleShowModal,
  }));

  return (
    <>
      <Modal
        width={'1690px'}
        title="Split Ticket"
        className='modal-split-ticket modal-non-opacity'
        visible={open}
        onClose={handleCloseModal}
        onOk={handleSubmitModal}
        footer={null}
        okText="Done"
      >
        <Box style={{ background: '#F5F5F5', padding: '1rem' }}>
          <DndContext onDragEnd={handleDragEnd}>
            <TicketContainerStyled className={'v2'}>
              <TicketDisplay v2 item={ticketDetail} />
              <div className='flex-grow' ref={scrollRef}>
                {newTickets.map(o => (<TicketDisplay v2 key={o.billId} item={o} />))}
              </div>
            </TicketContainerStyled>
            <DragOverlay />
          </DndContext>
          <Footer>
            <Button block type_cancel onClick={handleCloseModal}>
              Close
            </Button>
            <Button block type_tertiary onClick={handleAddNewTicket}>
              Add More Ticket
            </Button>
            <Button block type_primary onClick={handleSubmitModal}>
              OK
            </Button>
          </Footer>
        </Box>
      </Modal>
    </>
  );
}
);
ModalSplitTicket.displayName = 'ModalSplitTicket';
export default ModalSplitTicket;
const Footer = styled(FooterActions)`
  button {
    flex: 1;
  }
`;
const TicketContainerStyled = styled.div`
  padding-top: 1.5rem;
  padding-bottom: 1rem;
  display: flex;
  position: relative;
  .ticket-item {
    width: 33.33%;
    min-width: 33.33%;
    height: 21.5rem;
    position:relative;
    z-index:2;
    -webkit-user-select: none;
    /* Safari */
    -ms-user-select: none;
    /* IE 10 and IE 11 */
    user-select: none;
    /* Standard syntax */
  }

  .flex-grow {
    overflow: auto;
    flex-grow: 1;
    display: flex;
    flex-wrap: nowrap;
    .ticket-item {
      width: 50%;
      min-width: 50%;
      position:relative;
      z-index:1;
    }  
  }
  
  .ticket-item:last-child {
    .ticket-wrapper {
      margin-right: 0;
    }
  }
  .ticket-wrapper {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.5rem;
    border-radius: 5px;
  }

  &.v2 {
    padding-top: 0px;
   .ticket-item {
      width: 25%;
      min-width: 25%;
      height: unset;
      min-height: 40rem;
      padding-bottom: 8px;
   }

   .flex-grow {
      .ticket-item {
        width: 33.3%;
        min-width: 33.3%;
      }  
    }
  }
`;

const TicketStyled = styled.div<{ overflow: boolean }>`
  margin-right: 1rem;
  min-height: calc(100% - 1rem);
  // overflow-y:${({ overflow }) => overflow ? 'auto' : 'unset'}

  &.ticket-wrapper.v2 {
    border-radius: 4px;
    background: #F6F7FC;
    height: 100%;
    min-height: 40rem;
    padding: 0;
    box-shadow: 0px 2px 4px 0px rgba(44, 35, 66, 0.40), 0px 4px 8px -3px rgba(45, 35, 66, 0.30), 0px -3px 0px 0px #D6D6E7 inset;
    .ticket-header {
      background: #FCFCFD;
      box-shadow: 0px 4px 8px -4px rgba(0, 0, 0, 0.25);
      display: flex;
      padding: 12px;
      justify-content: space-between;
      align-items: center;
      align-self: stretch;
      .ticket-number {
        display: flex;
        padding: 0px 8px;
        justify-content: center;
        align-items: center;
        gap: 10px;
        border-radius: 5px;
        background: #232F3E;
        height: 42px;
        &.H9 {
          color: #fff;
          font-size: 28px;
        }
      }
      .H9 {
        height: 42px;
        color: #1D2129;
        text-align: center;
        font-family: Poppins;
        font-size: 24px;
        font-style: normal;
        font-weight: 600;
        line-height: normal;
        display: flex;
        align-items: center;
      }
    }

    .service-item {
      margin: 0 16px;
      .BODY_1 {
        font-size: 18px;
      }
    }
  }
`;

const TicketDisplay = ({ item, v2 }: { item: ISplitTicketItem | null, v2?: boolean; }) => {
  const TicketContent = useCallback(() => {
    if (!item) return null;
    const total = sumBy(item.items, o => (o.price * o.quantity) - o.discount) || 0;
    return (
      <TicketStyled className={clsx('ticket-wrapper', v2 && 'v2')} overflow={!!item.isDraft}>
        <Box className="space-between ticket-header">
          {!item.isDraft ? <Text className='ticket-number' color_text_1 title_h7_20_semibold>
            #{item.ticketNumber || 0}
          </Text> : <Text className='ticket-number' color_text_1 title_h7_20_semibold >New</Text>}
          <Text title_h7_20_semibold>
            {formatCurrency(total)}
          </Text>
        </Box>
        {/* mapping here */}
        {item.items.map((service, index) => {
          const col: DraggableData = { service };
          return (
            item.isDraft ?
              <Box display='flex' padding={'8px 16px'}>

                <Text content_content_1_16_regular style={{ flex: 1, marginLeft: 12 }}>
                  {service.itemName}
                </Text>
                <Box display="flex" gap="2" alignItems="center">
                  <Text content_content_2_16_medium>
                    {formatCurrency(service.price)}
                  </Text>
                </Box>
              </Box> :
              <Draggable key={service.uuid_local} id={`${service.itemId}_${index}`} data={col}>
                <Box display='flex' padding={'8px 16px'}>
                  <svg viewBox="0 0 20 20" width="12" >
                    <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z"
                      // @ts-ignore
                      fill={'#2D3043'}
                    ></path>
                  </svg>
                  <Text content_content_1_16_regular style={{ flex: 1, marginLeft: 12 }}>
                    {service.itemName}
                  </Text>
                  <Box display="flex" gap="2" alignItems="center">
                    <Text content_content_2_16_medium>
                      {formatCurrency(service.price)}
                    </Text>
                  </Box>
                </Box>
              </Draggable>
          );
        })}
      </TicketStyled>
    );
  }, [item]);

  if (!item?.isDraft) return <div className='ticket-item'>
    <TicketContent />
  </div>;

  return (
    <Droppable
      id={item.billId}
      className="ticket-item"
    >
      <TicketContent />
    </Droppable>
  );
};
